import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, eur, kindLabel, API } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { StatusBadge } from "@/pages/AdminDashboard";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Flag, FileDown, RefreshCw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function AdminBookingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/bookings/${id}`);
      setBooking(data);
    } catch (e) {
      toast.error("Réservation introuvable");
      nav("/admin/dashboard");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const action = async (path, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${id}/${path}`, extra);
      toast.success("Action effectuée");
      setBooking(data.booking ?? data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBusy(false);
    }
  };

  const paySolde = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${id}/solde-checkout`, {
        origin_url: window.location.origin,
      });
      window.open(data.checkout_url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBusy(false);
    }
  };

  if (!booking) return null;
  const b = booking;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-booking-detail">
      <AdminHeader />
      <main className="max-w-6xl mx-auto pt-28 pb-16 px-6 lg:px-10">
        <Link to="/admin/dashboard" data-testid="btn-back-dashboard" className="text-gray-400 hover:text-white inline-flex items-center gap-2 text-sm">
          <ArrowLeft size={14} /> Retour au tableau de bord
        </Link>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.5em] gold uppercase">Réservation</div>
            <h1 className="mt-2 font-serif text-white text-4xl">{b.reference}</h1>
            <div className="mt-1 text-gray-400">{kindLabel(b.booking_type)}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={b.status} />
            <div className="text-xs text-gray-500">
              Créée le {new Date(b.created_at).toLocaleString("fr-FR")}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-10">
          <div className="space-y-6">
            {/* Details block */}
            <Block title="Client">
              <RowKV k="Nom" v={b.client.full_name} />
              <RowKV k="Email" v={b.client.email} />
              <RowKV k="Téléphone" v={b.client.phone} />
            </Block>

            {b.convoyage && (
              <Block title="Véhicule & Trajet">
                <RowKV k="Marque / Modèle" v={`${b.convoyage.brand} ${b.convoyage.model}`} />
                <RowKV k="Catégorie" v={b.convoyage.category} />
                <RowKV k="État" v={b.convoyage.state} />
                <RowKV k="Boîte" v={b.convoyage.gearbox} />
                <RowKV k="Départ" v={b.convoyage.pickup.label} />
                <RowKV k="Arrivée" v={b.convoyage.dropoff.label} />
                <RowKV k="Distance" v={`${b.convoyage.distance_km} km`} />
                <RowKV k="Date" v={b.convoyage.preferred_date} />
                {b.convoyage.notes && <RowKV k="Notes" v={b.convoyage.notes} />}
              </Block>
            )}

            {b.vtc_trip && (
              <Block title="Trajet">
                <RowKV k="Départ" v={b.vtc_trip.pickup.label} />
                <RowKV k="Arrivée" v={b.vtc_trip.dropoff.label} />
                <RowKV k="Distance" v={`${b.vtc_trip.distance_km} km`} />
                <RowKV k="Date & heure" v={new Date(b.vtc_trip.pickup_datetime).toLocaleString("fr-FR")} />
                <RowKV k="Passagers" v={b.vtc_trip.passengers} />
                {b.vtc_trip.notes && <RowKV k="Notes" v={b.vtc_trip.notes} />}
              </Block>
            )}

            {b.vtc_dispo && (
              <Block title="Mise à disposition">
                <RowKV k="Départ" v={b.vtc_dispo.pickup.label} />
                <RowKV k="Début" v={new Date(b.vtc_dispo.start_datetime).toLocaleString("fr-FR")} />
                <RowKV k="Durée" v={`${b.vtc_dispo.duration_hours} h`} />
                <RowKV k="Passagers" v={b.vtc_dispo.passengers} />
                {b.vtc_dispo.notes && <RowKV k="Notes" v={b.vtc_dispo.notes} />}
              </Block>
            )}

            {b.cleaning && (
              <Block title="Nettoyage automobile">
                <RowKV k="Formule" v={{
                  express: "Express",
                  interieur: "Intérieur",
                  exterieur: "Extérieur",
                  complete: "Complète",
                  premium: "Premium",
                }[b.cleaning.formula] || b.cleaning.formula} />
                <RowKV k="Véhicule" v={`${b.cleaning.brand} ${b.cleaning.model}`} />
                <RowKV k="Type" v={b.cleaning.vehicle_type} />
                {b.cleaning.license_plate && <RowKV k="Immatriculation" v={b.cleaning.license_plate} />}
                <RowKV k="Adresse d'intervention" v={b.cleaning.address.label} />
                <RowKV k="Date & heure" v={new Date(b.cleaning.scheduled_datetime).toLocaleString("fr-FR")} />
                {b.cleaning.options?.length > 0 && (
                  <RowKV k="Options" v={b.cleaning.options.join(", ")} />
                )}
                {b.cleaning.notes && <RowKV k="Notes" v={b.cleaning.notes} />}
              </Block>
            )}

            <Block title="Paiement">
              <RowKV k="Total" v={eur(b.price.total)} />
              {b.price.surcharges?.map((s, i) => (
                <RowKV key={i} k={s.label} v={eur(s.amount)} />
              ))}
              <RowKV k={b.price.payment_mode === "full" ? "Payé (100%)" : `Acompte (${b.price.deposit_percent}%)`} v={eur(b.price.deposit_amount)} />
              {b.price.payment_mode !== "full" && (
                <RowKV k="Solde restant" v={eur(b.price.total - b.price.deposit_amount)} />
              )}
              <RowKV k="Statut paiement" v={b.payment_status} />
              {b.stripe_payment_intent_id && <RowKV k="Payment Intent" v={b.stripe_payment_intent_id} />}
            </Block>

            {b.refuse_reason && (
              <div className="border border-red-500/30 bg-red-500/5 p-5">
                <div className="text-xs tracking-widest uppercase text-red-300">Motif du refus</div>
                <div className="mt-2 text-red-200">{b.refuse_reason}</div>
              </div>
            )}
          </div>

          {/* Actions sidebar */}
          <aside className="lg:sticky lg:top-28 h-fit space-y-4">
            <div className="bg-[#0d0d0d] border border-white/10 p-6">
              <div className="text-xs tracking-widest gold uppercase mb-4">Actions</div>

              {b.status === "awaiting_validation" && (
                <>
                  <button
                    data-testid="btn-confirm"
                    disabled={busy}
                    onClick={() => action("confirm", {})}
                    className="btn-gold w-full inline-flex items-center justify-center gap-2 mb-2"
                  >
                    <CheckCircle2 size={16} /> Valider & Confirmer
                  </button>
                  <button
                    data-testid="btn-refuse"
                    disabled={busy}
                    onClick={() => setRefuseOpen(true)}
                    className="btn-outline-gold w-full inline-flex items-center justify-center gap-2 border-red-500/50 text-red-300 hover:bg-red-500/10"
                  >
                    <XCircle size={16} /> Refuser & Rembourser
                  </button>
                </>
              )}

              {b.status === "confirmed" && (
                <>
                  <button
                    data-testid="btn-complete"
                    disabled={busy}
                    onClick={() => action("complete", {})}
                    className="btn-gold w-full inline-flex items-center justify-center gap-2 mb-2"
                  >
                    <Flag size={16} /> Marquer terminée
                  </button>
                  {b.price.payment_mode !== "full" && (
                    <button
                      data-testid="btn-solde-checkout"
                      disabled={busy}
                      onClick={paySolde}
                      className="btn-outline-gold w-full inline-flex items-center justify-center gap-2"
                    >
                      <RefreshCw size={16} /> Générer lien solde
                    </button>
                  )}
                </>
              )}

              {(b.status === "refused" || b.status === "completed") && (
                <div className="text-sm text-gray-500">Aucune action disponible.</div>
              )}

              <div className="border-t border-white/5 mt-5 pt-5 space-y-2">
                <a
                  data-testid="btn-devis-pdf"
                  href={`${API}/documents/${b.reference}/devis.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-gold w-full inline-flex items-center justify-center gap-2 text-sm"
                >
                  <FileDown size={14} /> Devis PDF
                </a>
                <a
                  data-testid="btn-facture-pdf"
                  href={`${API}/documents/${b.reference}/facture.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-gold w-full inline-flex items-center justify-center gap-2 text-sm"
                >
                  <FileDown size={14} /> Facture PDF
                </a>
              </div>
            </div>

            {b.calendar_event_id && (
              <div className="bg-[#0d0d0d] border border-white/10 p-4 text-xs text-gray-400">
                <div className="uppercase tracking-widest text-gray-500 mb-1">Google Calendar</div>
                <div>Event <span className="gold">{b.calendar_event_id}</span> (mock)</div>
              </div>
            )}
          </aside>
        </div>
      </main>

      <Dialog open={refuseOpen} onOpenChange={setRefuseOpen}>
        <DialogContent className="bg-[#0d0d0d] border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Refuser cette réservation ?</DialogTitle>
            <DialogDescription className="text-gray-400">
              Le paiement sera automatiquement remboursé et un email envoyé au client.
              Motif obligatoire pour la traçabilité.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="refuse-reason-input"
            value={refuseReason}
            onChange={(e) => setRefuseReason(e.target.value)}
            placeholder="Ex : véhicule non-conforme, indisponibilité..."
            className="bg-black border-white/10 text-white"
          />
          <DialogFooter>
            <button className="btn-outline-gold" onClick={() => setRefuseOpen(false)}>Annuler</button>
            <button
              data-testid="btn-refuse-confirm"
              disabled={!refuseReason.trim() || busy}
              className="btn-gold bg-red-500 hover:bg-red-400"
              style={{ background: "#dc2626", color: "#fff" }}
              onClick={async () => {
                await action("refuse", { reason: refuseReason });
                setRefuseOpen(false);
                setRefuseReason("");
              }}
            >
              Confirmer le refus
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="bg-[#0d0d0d] border border-white/10 p-6">
      <div className="text-xs tracking-widest gold uppercase mb-4">{title}</div>
      <div className="divide-y divide-white/5">{children}</div>
    </section>
  );
}

function RowKV({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-2 text-sm">
      <div className="text-gray-500">{k}</div>
      <div className="text-white text-right">{String(v)}</div>
    </div>
  );
}
